import { useState, type JSX } from 'react';

import { useCollapsibleSection } from 'shared/hooks/collapsibleSections';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';

import { useGetWhatsAppCallingSettingsQuery, useUpdateWhatsAppCallingSettingsMutation } from '../services/projects.api';

interface Props {
  projectId: number;
}

const WhatsAppCallingSection = ({ projectId }: Props): JSX.Element => {
  const sectionProps = useCollapsibleSection('whatsapp-calling');
  const { data, isLoading, error, refetch } = useGetWhatsAppCallingSettingsQuery(projectId);
  const [updateSettings, { error: updateError, isLoading: isUpdating }] = useUpdateWhatsAppCallingSettingsMutation();
  const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null);
  const enabled = data?.localEnabled === true && data.status === 'ENABLED';
  const remoteStatus =
    data?.status === 'ENABLED'
      ? 'Llamadas habilitadas'
      : data?.status === 'DISABLED'
        ? 'Llamadas deshabilitadas'
        : 'No disponible';

  const handleConfirm = async (): Promise<void> => {
    if (pendingEnabled === null) return;
    try {
      await updateSettings({ projectId, enabled: pendingEnabled }).unwrap();
      setPendingEnabled(null);
    } catch {
      // RTK Query exposes the rejection through updateError for inline feedback.
    }
  };

  return (
    <>
      <Accordion
        type="single"
        collapsible
        {...sectionProps}
        className="bg-bg-surface border border-border-subtle rounded-xl"
      >
        <AccordionItem value="content" className="border-none">
          <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-bg-surface-raised rounded-xl data-[state=open]:rounded-b-none">
            <span className="flex items-center gap-3">
              <span className="font-semibold text-text-default">Llamadas de WhatsApp</span>
              {data && (
                <Badge className={enabled ? 'bg-success-light text-success' : 'bg-neutral-100 text-text-subtle'}>
                  {enabled ? 'Habilitadas' : 'Deshabilitadas'}
                </Badge>
              )}
            </span>
          </AccordionTrigger>
          <AccordionContent className="px-5 pb-5 border-t border-border-subtle">
            <div className="flex flex-col gap-4 pt-4">
              <div className="flex items-center justify-between gap-4">
                <p className="text-sm text-text-muted">
                  Habilita solicitudes de permiso para llamadas. El ícono de llamada permanecerá oculto para todos los
                  clientes.
                </p>
                {data && (
                  <Switch
                    checked={enabled}
                    disabled={isUpdating}
                    aria-label="Habilitar llamadas de WhatsApp"
                    onCheckedChange={(checked) => setPendingEnabled(checked)}
                  />
                )}
              </div>

              {isLoading && <p className="text-sm text-text-subtle">Consultando configuración en WhatsApp...</p>}
              {data && (
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div>
                    <dt className="text-text-subtle">Proveedor</dt>
                    <dd className="text-text-default">{data.provider}</dd>
                  </div>
                  <div>
                    <dt className="text-text-subtle">Configuración en WhatsApp</dt>
                    <dd className="text-text-default">{remoteStatus}</dd>
                  </div>
                </dl>
              )}
              {error && (
                <div className="flex items-center justify-between gap-3 text-sm text-error">
                  <span>{extractErrorMessage(error, 'No se pudo consultar la configuración de WhatsApp.')}</span>
                  <Button variant="outline" size="sm" onClick={() => void refetch()}>
                    Reintentar
                  </Button>
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <AlertDialog open={pendingEnabled !== null} onOpenChange={(open) => !open && setPendingEnabled(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingEnabled ? '¿Habilitar llamadas de WhatsApp?' : '¿Deshabilitar llamadas de WhatsApp?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingEnabled
                ? 'Se habilitarán las solicitudes de permiso para llamadas. El ícono de llamada seguirá oculto para todos los clientes.'
                : 'Se deshabilitarán las llamadas en WhatsApp y la función de solicitud de permiso en el proyecto.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {updateError && (
            <p role="alert" className="text-sm text-error">
              {extractErrorMessage(updateError, 'No se pudo actualizar la configuración de llamadas.')}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUpdating}>Cancelar</AlertDialogCancel>
            <Button disabled={isUpdating} onClick={() => void handleConfirm()}>
              {isUpdating ? 'Actualizando...' : 'Confirmar'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default WhatsAppCallingSection;
